import { getByPath } from "@coworker/apprestructured/src/shared/helpers/object.helper";
import { useTranslation } from "@coworker/locales";
import { isLoading, LOADING, Loading } from "../loading";
import { useAllTaskTypes } from "./useTaskTypes";
import { Task } from "../types/Task";
import { getTypedValue } from "@coworker/apprestructured/src/shared/helpers/schemaValidator";
import { useFormatFieldValue } from "../hooks/useFormatFieldValue";
import { useFormatArrayFieldValue } from "../hooks/useFormatArrayFieldValue";

export const useCreateTitle = (
  task: Record<string, unknown> | undefined | Loading
): string | Loading => {
  const { t } = useTranslation();
  const formatFieldValue = useFormatFieldValue();
  const formatArrayFieldValue = useFormatArrayFieldValue();

  const { data: taskTypes, isLoading: isTaskTypesLoading } = useAllTaskTypes();

  const titleCreator = taskTypes?.find(
    (type) => type.id === (task as Task)?.task_type_id
  )?.title_creator;

  if (
    isLoading(task) ||
    isTaskTypesLoading ||
    !titleCreator ||
    task === undefined
  ) {
    return LOADING;
  }

  const { id, props } = titleCreator;

  switch (id) {
    case "field-title-creator": {
      const { field, shouldTranslateText } = props;
      const fieldValue = getTypedValue<string>(getByPath(task, field));
      if (!fieldValue || isLoading(fieldValue)) {
        return LOADING;
      }
      return shouldTranslateText ? t(fieldValue) : fieldValue;
    }
    case "translation-title-creator": {
      const { translationKey, translationProps } = props;
      const tProps: Record<string, string> = {};
      if (translationProps) {
        for (const [key, config] of Object.entries(translationProps)) {
          const { format, valueField } = config;
          const rawValue = getTypedValue<unknown>(getByPath(task, valueField));

          if (isLoading(rawValue)) return LOADING;

          let processedValue: string | string[];
          // if array contains objects, extract "value"
          const storeValueOnly =
            Array.isArray(rawValue) &&
            rawValue.length > 0 &&
            !rawValue.every((item) => typeof item === "string");
          if (Array.isArray(rawValue) && !storeValueOnly) {
            processedValue = rawValue.map((item) =>
              typeof item === "object" && item !== null
                ? String((item as Record<string, unknown>)["value"] ?? "")
                : String(item)
            );
          } else {
            processedValue = rawValue as string | string[];
          }

          const formattedValue = Array.isArray(processedValue)
            ? formatArrayFieldValue(processedValue, format)
            : formatFieldValue(processedValue, format);

          tProps[key] = formattedValue;
        }
      }
      return t(translationKey, tProps);
    }
    case "conditional-title-creator": {
      const { condition, whenTrue, whenFalse } = props;
      const conditionValue = getTypedValue<string>(
        getByPath(task, condition.field)
      );

      if (isLoading(conditionValue)) {
        return LOADING;
      }

      const config = conditionValue === condition.equals ? whenTrue : whenFalse;
      const fieldValue = getTypedValue<string>(getByPath(task, config.field));

      if (isLoading(fieldValue)) {
        return LOADING;
      }

      return config.shouldTranslateText ? t(fieldValue) : fieldValue;
    }
  }
};
